/**
 * SwitchWearableModal — connect / switch / disconnect a health wearable.
 * No role=dialog by design.
 */
import { createPortal } from 'react-dom';
import { WEARABLE_OPTIONS } from '../constants';

interface SwitchWearableModalProps {
  activeWearable: any;
  connecting: string | false;
  onConnectProvider: (provider: string, label: string) => void;
  onDisconnectWearable: () => void;
  onClose: () => void;
}

export function SwitchWearableModal({
  activeWearable,
  connecting,
  onConnectProvider,
  onDisconnectWearable,
  onClose,
}: SwitchWearableModalProps) {
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)', borderRadius: 12, padding: '20px 24px', width: 320,
          maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          fontFamily: "'DM Sans', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 4 }}>
          {activeWearable ? 'Switch wearable' : 'Connect a wearable'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 14 }}>
          {activeWearable
            ? `Currently connected to ${activeWearable.provider.charAt(0).toUpperCase() + activeWearable.provider.slice(1)}. Only one wearable can be active at a time.`
            : 'Choose a wearable to sync your health data.'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {WEARABLE_OPTIONS.map((w) => {
            const isActive = activeWearable?.provider === w.provider;
            const isConnecting = connecting === w.provider;
            return (
              <button
                key={w.provider}
                onClick={() => {
                  if (isActive) return;
                  onConnectProvider(w.provider, w.name);
                  onClose();
                }}
                disabled={isActive || !!connecting}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, border: isActive ? `2px solid ${w.color}` : '1px solid var(--color-border, #DADCE0)',
                  background: isActive ? `${w.color}08` : 'transparent',
                  cursor: isActive ? 'default' : connecting ? 'wait' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
                  opacity: (!!connecting && !isConnecting) ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 7, background: w.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {w.logo}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{w.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{w.desc}</div>
                </div>
                {isActive && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,0.08)',
                    padding: '2px 8px', borderRadius: 4,
                  }}>
                    Connected
                  </span>
                )}
                {isConnecting && (
                  <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Connecting...</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
          {activeWearable ? (
            <button
              onClick={() => { onDisconnectWearable(); onClose(); }}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                border: '1px solid #fecaca', background: 'transparent',
                color: '#ef4444', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Disconnect
            </button>
          ) : <div />}
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
              border: '1px solid var(--color-border, #DADCE0)', background: 'transparent',
              color: 'var(--color-text-secondary)', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
